import { useEffect, useState } from "react";
import { HOME_MARKETS } from "../config/market";

export interface LiveMarketQuote {
  symbol: string;
  price: number;
  change: number;
  precision: number;
  icon: string;
  isPlatform: boolean;
}

async function fetchMarketQuotes() {
  const response = await fetch(
    "https://api.bitget.com/api/v2/spot/market/tickers",
    { headers: { Accept: "application/json" } },
  );
  if (!response.ok) throw new Error("market unavailable");
  const payload = (await response.json()) as {
    data?: Array<{
      symbol?: string;
      lastPr?: string;
      change24h?: string;
    }>;
  };
  const rows = new Map(
    (payload.data || []).map((row) => [String(row.symbol || ""), row]),
  );

  return HOME_MARKETS.flatMap((market) => {
    if (!market.bitgetSymbol) {
      return [
        {
          symbol: market.symbol,
          price: 0,
          change: 0,
          precision: market.precision,
          icon: market.icon,
          isPlatform: true,
        },
      ];
    }

    const row = rows.get(market.bitgetSymbol);
    const price = Number(row?.lastPr);
    const rawChange = Number(row?.change24h);
    if (!Number.isFinite(price) || !Number.isFinite(rawChange)) return [];
    return [
      {
        symbol: market.symbol,
        price,
        change: Math.abs(rawChange) <= 1 ? rawChange * 100 : rawChange,
        precision: market.precision,
        icon: market.icon,
        isPlatform: false,
      },
    ];
  });
}

export function useMarketQuotes() {
  const [quotes, setQuotes] = useState<LiveMarketQuote[]>([]);
  const [status, setStatus] = useState<"connecting" | "live" | "unavailable">(
    "connecting",
  );

  useEffect(() => {
    let active = true;

    const refresh = async () => {
      try {
        const next = await fetchMarketQuotes();
        if (!active || !next.length) return;
        setQuotes(next);
        setStatus("live");
      } catch {
        if (active) {
          setStatus((current) =>
            current === "connecting" ? "unavailable" : current,
          );
        }
      }
    };

    void refresh();
    const refreshTimer = window.setInterval(refresh, 15000);

    return () => {
      active = false;
      window.clearInterval(refreshTimer);
    };
  }, []);

  return { quotes, status };
}
